import { createSlice,
         createAsyncThunk,  
         PayloadAction,
         ActionReducerMapBuilder,
         createSelector           } from '@reduxjs/toolkit';
import { WritableDraft            } from '@reduxjs/toolkit/node_modules/immer/dist/internal';

import Api,
       { ApiError                 } from 'src/services/api';
import * as types                   from 'src/services/api/types';
import Db                           from 'src/services/db';
import Util                         from 'src/services/util';
import { DefectType,
         InspectionStatusCode,
         ProductTypeKolpino,
         QCState                  } from 'src/services/api/types/inspection';

import { RootState                } from 'src/store';

import { AsyncOpStatus,
         Nullable                 } from 'src/common';



const SLICE_NAME = 'inspection:view';

type ExtraReducer = (builder: ActionReducerMapBuilder<InspectionViewState>) => void;
const extraReducers: ExtraReducer[] = [];

export interface InspectionViewState {
  loadOpStatus: AsyncOpStatus;
  loadOpStatusLabel: string;
  inspection: Nullable<types.inspection.Inspection>;
  defectRefer: types.refers.DefectReferElement[];
  
  account: Nullable<types.auth.Account>;

  viewOpened: boolean;
  viewFragment: 'defectlist' | 'piece' | 'complaintlist' | 'complaint';
  is404: boolean;

  defectExpandedGuid: string;
  pieceSelected: Nullable<types.inspection.Piece>;
  complaintDefectGuid: string;
  complaintSelected: Nullable<types.inspection.Complaint>;

  certDownloadOpened: boolean;
}

const initialState: InspectionViewState = {
  loadOpStatus: AsyncOpStatus.IDLE,
  loadOpStatusLabel: '',
  inspection: null,
  defectRefer: [],

  account: null,

  viewOpened: false,
  viewFragment: 'defectlist',
  is404: false,

  defectExpandedGuid: '',
  pieceSelected: null,
  complaintDefectGuid: '',
  complaintSelected: null,

  certDownloadOpened: false,
}

// #region ОБЩЕЕ

// #region openForViewAsync
type OpenForViewArgs = {
  viewGuid: string;
};
type OpenForViewResolve = {
  inspection: Nullable<types.inspection.Inspection>;
  account: Nullable<types.auth.Account>;
  defectExpandedGuid: string;
  defectRefer: types.refers.DefectReferElement[];
  is404: boolean;
};
export type OpenForViewReject = ApiError;
export const openForViewAsync = createAsyncThunk<
  OpenForViewResolve,
  OpenForViewArgs,
  {
    state: RootState,
    rejectValue: OpenForViewReject,
  }
>(
  `${SLICE_NAME}/openForViewAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.viewOpened());
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Получение осмотра'));

      const account = thunkAPI.getState().authCommon.account;

      let remoteInspection: Nullable<types.inspection.Inspection> = null;
      let is404: boolean = false;

      try
      {
        remoteInspection = await Api.getInspectionByGuid({ guid: args.viewGuid });
      }
      catch (apiError)
      {
        if ((apiError as ApiError).statusCode === 404)
        {
          is404 = true;
        }
      }

      let localInspection: Nullable<types.inspection.Inspection> = null;

      try
      {
        localInspection = (await Db.getInspections()).find((item) => item.guid === args.viewGuid) ?? null;
      }
      catch (dbError) { }

      let resultInspection: Nullable<types.inspection.Inspection> = null;

      if (remoteInspection === null && localInspection === null)
      {
        resultInspection = null;
      }
      else if (remoteInspection !== null && localInspection === null)
      {
        resultInspection = JSON.parse(JSON.stringify(remoteInspection));
      }
      else if (localInspection !== null && (remoteInspection === null || remoteInspection.timestampModified <= localInspection.timestampModified))
      {
        resultInspection = JSON.parse(JSON.stringify(localInspection));
      }
      else if (localInspection != null && remoteInspection !== null && remoteInspection.timestampModified > localInspection.timestampModified) {
        resultInspection = JSON.parse(JSON.stringify(localInspection));
      }

      const defectRefer = await Db.getDefects();

      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));

      return {
        inspection: resultInspection,
        account,
        defectExpandedGuid: resultInspection?.inspectionDefects[0]?.guid ?? '',
        defectRefer,
        is404: resultInspection === null && is404,
      }
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(openForViewAsync.fulfilled, (state, action) => {
      state.loadOpStatusLabel = '';
      state.inspection = action.payload.inspection;
      state.account = action.payload.account;
      state.defectRefer = action.payload.defectRefer;

      state.viewFragment = 'defectlist';
      state.is404 = action.payload.is404;

      state.defectExpandedGuid = action.payload.defectExpandedGuid;
      state.pieceSelected = null;
    })
    .addCase(openForViewAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region downloadPhotoAsync
type DownloadPhotoArgs = {
  fileGuid: string;
};
type DownloadPhotoResolve = {
  fileGuid: string;
  dataUrl: string;
  binaryStr: string;
  name: string;
  mime: string;
};
export type DownloadPhotoReject = ApiError;
export const downloadPhotoAsync = createAsyncThunk<
  DownloadPhotoResolve,
  DownloadPhotoArgs,
  {
    state: RootState,
    rejectValue: DownloadPhotoReject,
  }
>(
  `${SLICE_NAME}/downloadPhotoAsync`,
  async (args, thunkAPI) => {
    const pE = thunkAPI.getState().inspectionView.pieceSelected;

    if (pE === null)
    {
      return {
        fileGuid: args.fileGuid,
        dataUrl: '',
        binaryStr: '',
        name: '',
        mime: '',
      }
    }
    else
    {
      let updatedFile: Nullable<types.inspection.PieceFile> = null;

      for (const file of pE.files)
      {
        if (file.guid === args.fileGuid)
        {
          updatedFile = file;
        }
      }

      if (updatedFile === null)
      {
        return {
          fileGuid: args.fileGuid,
          dataUrl: '',
          binaryStr: '',
          name: '',
          mime: '',
        }
      }
      else
      {
        try
        {
          const isTIF = Util.checkFileTypeByExt(updatedFile.name) === 'TIF';
          const mime = isTIF ? 'image/tiff' : 'image/jpeg';
          const remoteFile = await Api.getInspectionFileByGuid({ guid: args.fileGuid, source: 'piece' });
          const fileFromBlob = await Util.blobToFile(remoteFile.blob, updatedFile.name, mime);
          const dataUrlResult = await Util.imageToDataURL(fileFromBlob);
          const binaryStr = await Util.fileToBinaryString(fileFromBlob);

          if (dataUrlResult.ok)
          {
            return {
              fileGuid: args.fileGuid,
              dataUrl: dataUrlResult.result,
              binaryStr: binaryStr,
              name: updatedFile.name,
              mime,
            }
          }
          else
          {
            return {
              fileGuid: args.fileGuid,
              dataUrl: Util.noPhoto,
              binaryStr: '',
              name: updatedFile.name,
              mime: 'image/jpeg',
            }
          }
        }
        catch(error)
        {
          return {
            fileGuid: args.fileGuid,
            dataUrl: Util.noPhoto,
            binaryStr: '',
            name: updatedFile.name,
            mime: 'image/jpeg',
          }
        }
      }
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(downloadPhotoAsync.fulfilled, (state, action) => {
      if (state.pieceSelected !== null)
      {
        for (const file of state.pieceSelected.files)
        {
          if (file.guid === action.payload.fileGuid)
          {
            file.__dataUrl = action.payload.dataUrl;
            file.__binaryStr = action.payload.binaryStr;
            file.__name = action.payload.name;
            file.__mime = action.payload.mime;
          }
        }
      }

      if (state.inspection !== null)
      {
        for (const defect of state.inspection.inspectionDefects)
        {
          for (const piece of (defect.pieces || []))
          {
            for (const file of piece.files)
            {
              if (file.guid === action.payload.fileGuid)
              {
                file.__dataUrl = action.payload.dataUrl;
                file.__binaryStr = action.payload.binaryStr;
                file.__name = action.payload.name;
                file.__mime = action.payload.mime;
              }
            }
          }
        }
      }
    })
    .addCase(downloadPhotoAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region downloadCertAsync
type DownloadCertArgs = {
  qcNum: string;
};
type DownloadCertResolve = void;
export type DownloadCertReject = ApiError;
export const downloadCertAsync = createAsyncThunk<
  DownloadCertResolve,
  DownloadCertArgs,
  {
    state: RootState,
    rejectValue: DownloadCertReject,
  }
>(
  `${SLICE_NAME}/downloadCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Постановка в очередь задачи на скачивание сертификата'));
      
      await Api.postQCCheck({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(downloadCertAsync.fulfilled, (state, action) => {
      state.loadOpStatusLabel = '';
      state.certDownloadOpened = true;
      state.pieceSelected!.qmetData.qcState = QCState.LOADING;

      state.inspection!.inspectionDefects.forEach((defect) => {
        (defect.pieces ?? []).forEach((piece) => {
          if (piece.guid === state.pieceSelected!.guid) {
            piece.qmetData.qcState = QCState.LOADING;
          }
        })
      })
    })
    .addCase(downloadCertAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region checkCertAsync
type CheckCertArgs = {
  inspectionGuid: string;
};
type CheckCertResolve = QCState;
export type CheckCertReject = ApiError;
export const checkCertAsync = createAsyncThunk<
  CheckCertResolve,
  CheckCertArgs,
  {
    state: RootState,
    rejectValue: CheckCertReject,
  }
>(
  `${SLICE_NAME}/checkCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Проверка статуса загрузки сертификата'));
      
      const inspection = await Api.getInspectionByGuid({ guid: args.inspectionGuid });
      const selectedPiece = selectors.selectPieceSelected(thunkAPI.getState());

      const updatedDefect = inspection.inspectionDefects.find((defect) => (defect.pieces || []).find((piece) => piece.guid === selectedPiece!.guid) !== undefined)!;
      const updatedPiece = (updatedDefect.pieces || []).find((piece) => piece.guid === selectedPiece!.guid)!;

      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));

      return updatedPiece.qmetData.qcState;
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
extraReducers.push((builder) => {
  builder
    .addCase(checkCertAsync.fulfilled, (state, action) => {
      state.loadOpStatusLabel = '';
      state.pieceSelected!.qmetData.qcState = action.payload;

      state.inspection!.inspectionDefects.forEach((defect) => {
        (defect.pieces ?? []).forEach((piece) => {
          if (piece.guid === state.pieceSelected!.guid) {
            piece.qmetData.qcState = action.payload;
          }
        })
      })
    })
    .addCase(checkCertAsync.rejected, (state, action) => {
    });
});
// #endregion

// #region viewCertAsync
type ViewCertArgs = {
  qcNum: string;
};
type ViewCertResolve = void;
export type ViewCertReject = ApiError;
export const viewCertAsync = createAsyncThunk<
  ViewCertResolve,
  ViewCertArgs,
  {
    state: RootState,
    rejectValue: ViewCertReject,
  }
>(
  `${SLICE_NAME}/viewCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Получение файла сертификата'));
      
      const result = await Api.getQCFile({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));

      //window.open(result.dataUrl, '_blank')?.focus();
      const a = document.createElement("a");
      a.href = result.dataUrl;
      a.download = `cert_${args.qcNum}.pdf`;
      a.click();
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion

// #region shareCertAsync
type ShareCertArgs = {
  qcNum: string;
};
type ShareCertResolve = void;
export type ShareCertReject = ApiError;
export const shareCertAsync = createAsyncThunk<
  ShareCertResolve,
  ShareCertArgs,
  {
    state: RootState,
    rejectValue: ShareCertReject,
  }
>(
  `${SLICE_NAME}/shareCertAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Получение файла сертификата'));
      
      const result = await Api.getRawQCFile({ qcNum: args.qcNum });

      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));

      const file = new File([result.rawData], `Сертификат №${args.qcNum}.pdf`, { type: 'application/pdf' });
      navigator.share({
        title: `Сертификат №${args.qcNum}.pdf`,
        text: `Файл сертификата из приложения Checksteel`,
        files: [file]
      });
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion

// #region viewDOCAsync
type ViewDOCArgs = {
  fileName: string;
  guid: string;
  source: 'piece' | 'inspection';
};
type ViewDOCResolve = void;
export type ViewDOCReject = ApiError;
export const viewDOCAsync = createAsyncThunk<
  ViewDOCResolve,
  ViewDOCArgs,
  {
    state: RootState,
    rejectValue: ViewDOCReject,
  }
>(
  `${SLICE_NAME}/viewDOCAsync`,
  async (args, thunkAPI) => {
    try
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.BUSY));
      thunkAPI.dispatch(inspectionViewActions.laodOpStatusLabelChanged('Получение документа'));

      let resultDataUrl = '';

      switch (Util.getFileExt(args.fileName).toUpperCase()) {
        case 'PDF':
          resultDataUrl = (await Api.getInspectionFileAsPDFByGuid({ guid: args.guid, source: args.source })).dataUrl;
          break;
        case 'XLSX':
          resultDataUrl = (await Api.getInspectionFileAsXLSXByGuid({ guid: args.guid, source: args.source })).dataUrl;
          break;
      }
      
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.SUCCESS));

      const a = document.createElement("a");
      a.href = resultDataUrl;
      a.download = args.fileName;
      a.click();
    }
    catch (error)
    {
      thunkAPI.dispatch(inspectionViewActions.loadOpStatusChanged(AsyncOpStatus.ERROR));
      return thunkAPI.rejectWithValue(error as ApiError);
    }
  }
);
// #endregion


const reducers = {
  loadOpStatusChanged: (state: WritableDraft<InspectionViewState>, action: OpStatusChangedAction) => {
    state.loadOpStatus = action.payload;
  },

  laodOpStatusLabelChanged: (state: WritableDraft<InspectionViewState>, action: OpStatusLabelChangedAction) => {
    state.loadOpStatusLabel = action.payload;
  },

  viewOpened: (state: WritableDraft<InspectionViewState>) => {
    state.viewOpened = true;
  },

  viewClosed: (state: WritableDraft<InspectionViewState>) => {
    state.viewOpened = false;
  },

  changeViewFragment: (state: WritableDraft<InspectionViewState>, action: FragmentChangedAction) => {
    state.viewFragment = action.payload;
  },

  changeDefectExpandedGuid: (state: WritableDraft<InspectionViewState>, action: StringChangedAction) => {
    state.defectExpandedGuid = action.payload;
  },

  changePieceSelected: (state: WritableDraft<InspectionViewState>, action: PieceChangedAction) => {
    state.pieceSelected = action.payload;
    state.viewFragment = 'piece';
  },

  changeComplaintSelected: (state: WritableDraft<InspectionViewState>, action: ComplaintChangedAction) => {
    state.complaintSelected = action.payload.complaint;
    state.complaintDefectGuid = action.payload.complaintDefectGuid;
    state.viewFragment = 'complaint';
  },

  defectExpandedGuidChanged: (state: WritableDraft<InspectionViewState>, action: StringChangedAction) => {
    state.defectExpandedGuid = action.payload;
  },

  certDownloadOpened: (state: WritableDraft<InspectionViewState>) => {
    state.certDownloadOpened = true;
  },

  certDownloadClosed: (state: WritableDraft<InspectionViewState>) => {
    state.certDownloadOpened = false;
  },
}

const selectors = {
  selectViewOpened: (state: RootState) => state.inspectionView.viewOpened,
  selectIs404: (state: RootState) => state.inspectionView.is404,
  selectViewFragment: (state: RootState) => state.inspectionView.viewFragment,
  selectInspectionNumber: (state: RootState) => state.inspectionView.inspection?.inspectionNum ?? '',
  selectInspectionGuid: (state: RootState) => state.inspectionView.inspection?.guid ?? '',
  selectLoadOpStatus: (state: RootState) => state.inspectionView.loadOpStatus,
  selectLoadOpStatusLabel: (state: RootState) => state.inspectionView.loadOpStatusLabel,
  selectDefectExpandedGuid: (state: RootState) => state.inspectionView.defectExpandedGuid,
  selectPieceSelected: (state: RootState) => state.inspectionView.pieceSelected,
  selectIsEntryAccounting: (state: RootState) => state.inspectionView.inspection?.isEntryAccounting ?? false,
  selectDefectAllCount: (state: RootState) => state.inspectionView.inspection?.inspectionDefects?.length ?? 0,
  selectDefects: (state: RootState) => state.inspectionView.inspection?.inspectionDefects ?? [],
  selectDefectRefer: (state: RootState) => state.inspectionView.defectRefer,

  selectDefectDefectConfigs: createSelector(
    [
      (state: RootState, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionView.inspection?.inspectionDefects ?? [];
      const defectId = allDefects.find((defect) => defect.guid === defectGuid)?.defectId ?? null;

      if (defectId !== null)
      {
        const defectReferElement = state.inspectionView.defectRefer.find((item) => item.id === defectId) ?? null;

        return defectReferElement?.configs ?? [];
      }

      return [];
    }
  ),

  selectDefectFiles: createSelector(
    [
      (state: RootState, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return null;
      }

      return defect.files;
    }
  ),

  selectDefectDefectType: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionView.inspection?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.defectType ?? null;
    }
  ),

  selectDefectPriorityTitle: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const allDefects = state.inspectionView.inspection?.inspectionDefects ?? [];

      return allDefects.find((defect) => defect.guid === defectGuid)?.priorityTitle ?? null;
    }
  ),

  selectDefectQmetIds: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const qmetIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (!piece.isPiece && piece.qmetId != null && piece.qmetId > 0)
        {
          qmetIds.push(`${piece.qmetId}`);
        }
      })

      return qmetIds.join(', ');
    }
  ),

  selectDefectPieceIds: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const pieceIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (piece.isPiece && piece.heat != null && piece.piece != null && piece.heat !== '' && piece.piece.trim() !== '' && (piece.groupAtt === null || piece.groupAtt === ''))
        {
          pieceIds.push(`${piece.heat}/${piece.piece}`);
        }
      })

      return pieceIds.join(', ');
    }
  ),

  selectDefectGroupAttIds: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const groupAttIds: string[] = [];

      (defect.pieces || []).forEach((piece) => {
        if (piece.isPiece && piece.groupAtt != null && piece.groupAtt !== '')
        {
          if (groupAttIds.length === 0)
          {
            if (piece.heat != null && piece.heat !== '') 
            {
              groupAttIds.push(`${piece.heat}/`);
            }
            if (piece.groupAtt != null && piece.groupAtt !== '' && piece.qcNum != null && piece.qcNum !== '')
            {
              groupAttIds.push(`${piece.groupAtt}/${piece.qcNum}`);
            }
            else
            {
              if (piece.groupAtt != null && piece.groupAtt !== '')
              {
                groupAttIds.push(`${piece.groupAtt}`);
              }
              else
              {
                groupAttIds.push(`${piece.qcNum}`);
              }
            }
          }
        }
      })

      return groupAttIds.join(', ');
    }
  ),

  selectDefectKolpinoMechanicalEngineeringParts: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const mechanicalEngineeringParts: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.MECHANICAL_ENGINEERING_PART)
        .forEach((piece) => {
          mechanicalEngineeringParts.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return mechanicalEngineeringParts.join(', ');
    }
  ),

  selectDefectKolpinoProcessedSheets: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const processedSheets: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.PROCESSED_SHEET)
        .forEach((piece) => {
          processedSheets.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return processedSheets.join(', ');
    }
  ),

  selectDefectKolpinoUnprocessedSheets: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, defectGuid: string) => ({ state, defectGuid })
    ],
    ({ state, defectGuid }) => {
      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      if (!isKolpino)
      {
        return '';
      }

      const defect = (state.inspectionView.inspection?.inspectionDefects ?? [])
        .find((item) => item.guid === defectGuid) ?? null;

      if (defect === null)
      {
        return '';
      }

      const unprocessedSheets: string[] = [];

      (defect.pieces || [])
        .filter((piece) => piece.isPiece && piece.productTypeKolpinoId === ProductTypeKolpino.UNPROCESSED_SHEET)
        .forEach((piece) => {
          unprocessedSheets.push(getPieceTitle(piece, defect.defectType, isKolpino) ?? '');
        });

      return unprocessedSheets.join(', ');
    }
  ),


  selectPieceTitle: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const inspection = state.inspectionView.inspection;

      if (inspection === null)
      {
        return '';
      }

      const eD = inspection.inspectionDefects.find((defect) => defect.guid === state.inspectionView.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return '';
      }

      const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(eD.defectType);
      const isDirtyWagons = eD.defectType === DefectType.DIRTY_WAGONS;
      const isTerm = eD.defectType === DefectType.TERM;
      const productTypeKolpinoId = piece.productTypeKolpinoId;
      
      if (isQualityOrService)
      {
        if (piece.isPiece)
        {
          if (productTypeKolpinoId !== null)
          {
            switch (productTypeKolpinoId)
            {
              case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
              case ProductTypeKolpino.PROCESSED_SHEET:
                return 'Лист / Сертификат';
              case ProductTypeKolpino.UNPROCESSED_SHEET:
                return 'Плавка / Партия / Сертификат';
            }
          }
          else
          {
            const isGroupAttNotNull = piece.groupAtt !== null && piece.groupAtt !== '';
            const isQCNumNotNull = piece.qcNum !== null && piece.qcNum !== '';

            return (isGroupAttNotNull || isQCNumNotNull) ?
              'Плавка / Партия атт / Сертификат'
              :
              'Плавка / Партия/Ед';
          }
        }

        return 'Идентификационный №';
      }

      if (isDirtyWagons)
      {
        return '№ вагона';
      }

      if (isTerm)
      {
        return '№ спецификации';
      }
    }
  ),

  selectPieceText: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const inspection = state.inspectionView.inspection;

      if (inspection === null)
      {
        return '';
      }

      const eD = inspection.inspectionDefects.find((defect) => defect.guid === state.inspectionView.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return '';
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return '';
      }

      const account = state.inspectionView.account!;
      const isKolpino = (account.configs.find((item) => item.code === 'KOLPINO')?.value ?? '0') === '1';

      return getPieceTitle(piece, eD.defectType, isKolpino);
    }
  ),

  selectPieceIsError: createSelector(
    [
      (state: Pick<RootState, "inspectionView">, pieceGuid: string) => ({ state, pieceGuid })
    ],
    ({ state, pieceGuid }) => {
      const inspection = state.inspectionView.inspection;

      if (inspection === null)
      {
        return false;
      }

      const eD = inspection.inspectionDefects.find((defect) => defect.guid === state.inspectionView.defectExpandedGuid) ?? null;
      
      if (eD === null)
      {
        return false;
      }

      const piece = (eD.pieces || []).find((piece) => piece.guid === pieceGuid) ?? null;

      if (piece === null)
      {
        return false;
      }

      const inspectionStatus = inspection.status.code;
      const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(eD.defectType);
      const isDirtyWagons = eD.defectType === DefectType.DIRTY_WAGONS;
      const isTerm = eD.defectType === DefectType.TERM;

      if (inspectionStatus === InspectionStatusCode.ERROR)
      {
        if (isQualityOrService && piece.pieceNumId === null)
        {
          return true;
        }

        if (isDirtyWagons && piece.carriageQcs.some((qc) => !qc.isControl))
        {
          return true;
        }

        if (isTerm && piece.specificationPoses.some((pos) => !pos.isControl))
        {
          return true;
        }
      }

      return false;
    }
  ),

  selectComplaintSelectedTypeComplaint: (state: RootState) => state.inspectionView.complaintSelected?.typeComplaint ?? null,
  selectComplaintDefectGuid: (state: RootState) => state.inspectionView.complaintDefectGuid,
  selectComplaintSelectedIsToScrap: (state: RootState) => state.inspectionView.complaintSelected?.isToScrap ?? null,
  selectComplaintSelectedScrapPrice: (state: RootState) => state.inspectionView.complaintSelected?.scrapPrice ?? null,
  selectComplaintSelectedIsEvaluateRolledMetal: (state: RootState) => state.inspectionView.complaintSelected?.isEvaluateRolledMetal ?? null,
  selectComplaintSelectedRequirementRolledMetall: (state: RootState) => state.inspectionView.complaintSelected?.requirementRolledMetall ?? null,
  selectComplaintSelectedSumReqRolledMetall: (state: RootState) => state.inspectionView.complaintSelected?.sumReqRolledMetall ?? null,
  selectComplaintSelectedIsToOffsetCosts: (state: RootState) => state.inspectionView.complaintSelected?.isToOffsetCosts ?? null,
  selectComplaintSelectedAmountOfCompensation: (state: RootState) => state.inspectionView.complaintSelected?.amountOfCompensation ?? null,
  selectComplaintSelectedIsReturnOfRolledMetal: (state: RootState) => state.inspectionView.complaintSelected?.isReturnOfRolledMetal ?? null,
  selectComplaintSelectedIsSale: (state: RootState) => state.inspectionView.complaintSelected?.isSale ?? null,
  selectComplaintSelectedSale: (state: RootState) => state.inspectionView.complaintSelected?.sale ?? null,
  selectComplaintSelectedIsRepresentative: (state: RootState) => state.inspectionView.complaintSelected?.isRepresentative ?? null,
  selectComplaintSelectedIsProvideResponse: (state: RootState) => state.inspectionView.complaintSelected?.isProvideResponse ?? null,
  selectComplaintSelectedIsIdentifyDangerous: (state: RootState) => state.inspectionView.complaintSelected?.isIdentifyDangerous ?? null,
  selectComplaintSelectedIsAnalizProblem: (state: RootState) => state.inspectionView.complaintSelected?.isAnalizProblem ?? null,
  selectComplaintSelectedIs8d: (state: RootState) => state.inspectionView.complaintSelected?.is8d ?? null,

  selectComplaintSelectedIsAddMetall: (state: Pick<RootState, "inspectionView">) => state.inspectionView.complaintSelected?.isAddMetall ?? null,
  selectComplaintSelectedIsInformDeadline: (state: Pick<RootState, "inspectionView">) => state.inspectionView.complaintSelected?.isInformDeadline ?? null,
  selectComplaintSelectedIsCompensatePenalty: (state: Pick<RootState, "inspectionView">) => state.inspectionView.complaintSelected?.isCompensatePenalty ?? null,
  selectComplaintSelectedPenalty: (state: Pick<RootState, "inspectionView">) => state.inspectionView.complaintSelected?.penalty ?? null,

  selectComplaintSelectedOtherReq: (state: RootState) => state.inspectionView.complaintSelected?.otherReq ?? null,
  selectComplaintSelectedInitiatorEnterpriseTitle: (state: RootState) => state.inspectionView.complaintSelected?.initiator?.enterpriseTitle ?? -1,
  selectComplaintSelectedInitiatorFIO: (state: RootState) => state.inspectionView.complaintSelected?.initiator?.fio ?? null,
  selectComplaintSelectedInitiatorPost: (state: RootState) => state.inspectionView.complaintSelected?.initiator?.post ?? null,
  selectComplaintSelectedInitiatorPhone: (state: RootState) => state.inspectionView.complaintSelected?.initiator?.phone ?? null,
  selectComplaintSelectedInitiatorEmail: (state: RootState) => state.inspectionView.complaintSelected?.initiator?.email ?? null,
  selectComplaintSelectedFIO: (state: RootState) => state.inspectionView.complaintSelected?.fio ?? null,
  selectComplaintSelectedEnterprise: (state: RootState) => state.inspectionView.complaintSelected?.enterprise ?? null,
  selectComplaintSelectedPhone: (state: RootState) => state.inspectionView.complaintSelected?.phone ?? null,
  selectComplaintSelectedEmail: (state: RootState) => state.inspectionView.complaintSelected?.email ?? null,
  selectComplaintSelectedGroupRevised: (state: RootState) => state.inspectionView.complaintSelected?.groupRevised ?? null,
  selectComplaintSelectedGroupRevisedValue: (state: RootState) => state.inspectionView.complaintSelected?.groupRevisedValue ?? null,
  selectComplaintSelectedGroupUnrevised: (state: RootState) => state.inspectionView.complaintSelected?.groupUnrevised ?? null,
  selectComplaintSelectedGroupUnrevisedValue: (state: RootState) => state.inspectionView.complaintSelected?.groupUnrevisedValue ?? null,
  selectComplaintEditedDecision: (state: RootState) => state.inspectionView.complaintSelected?.decision ?? null,

  selectCertDownloadOpened: (state: RootState) => state.inspectionView.certDownloadOpened,
}

const getPieceTitle = (piece: types.inspection.Piece, defectType: Nullable<DefectType>, isKolpino: boolean) => {
  const isGroupAttHasValue = piece.groupAtt !== null && piece.groupAtt !== '';
  const isQcNumHasValue = piece.qcNum !== null && piece.qcNum !== '';
  const isHeatHasValue = piece.heat !== null && piece.heat !== '';
  const isPieceHasValue = piece.piece !== null && piece.piece.trim() !== '';

  const isQualityOrService = [null, DefectType.QUALITY, DefectType.SERVICE].includes(defectType);
  const isDirtyWagons = defectType === DefectType.DIRTY_WAGONS;
  const isTerm = defectType === DefectType.TERM;
  const productTypeKolpinoId = piece.productTypeKolpinoId;

  const productType = (piece.qmetData.productType ?? '') !== '' ? `(${piece.qmetData.productType})` : '';

  if (isQualityOrService)
  {
    if (piece.isPiece)
    {
      if (isKolpino)
      {
        switch (productTypeKolpinoId)
        {
          case ProductTypeKolpino.MECHANICAL_ENGINEERING_PART:
          case ProductTypeKolpino.PROCESSED_SHEET:
            if (isPieceHasValue && isQcNumHasValue)
            {
              return `${piece.piece} / ${piece.qcNum} ${productType}`;
            }
            else
            {
              if (!isPieceHasValue && isQcNumHasValue)
              {
                return `Нет листа / ${piece.qcNum} ${productType}`;
              }
              else if (isPieceHasValue)
              {
                return `${piece.piece} / Нет сертиф ${productType}`;
              }
              else
              {
                return `Нет листа / Нет сертиф ${productType}`;
              }
            }
          case ProductTypeKolpino.UNPROCESSED_SHEET:
            if (isHeatHasValue && isGroupAttHasValue && isQcNumHasValue)
            {
              return `${piece.heat} / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
            }
            else
            {
              if (isHeatHasValue)
              {
                if (isGroupAttHasValue)
                {
                  return `${piece.heat} / ${piece.groupAtt} / Нет сертиф ${productType}`;
                }
                else
                {
                  return `${piece.heat} / Нет партии / ${piece.qcNum} ${productType}`;
                }
              }
              else
              {
                if (isGroupAttHasValue && isQcNumHasValue)
                {
                  return `Нет плавки / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
                }
                else
                {
                  if (isGroupAttHasValue)
                  {
                    return `Нет плавки / ${piece.groupAtt} / Нет сертиф ${productType}`;
                  }
                  else
                  {
                    return `Нет плавки / Нет партии / ${piece.qcNum} ${productType}`;
                  }
                }
              }
            }
        }
      }
      else
      {
        if (isGroupAttHasValue || isQcNumHasValue)
        {
          if (isHeatHasValue && isGroupAttHasValue && isQcNumHasValue)
          {
            return `${piece.heat} / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
          }
          else
          {
            if (isHeatHasValue)
            {
              if (isGroupAttHasValue)
              {
                return `${piece.heat} / ${piece.groupAtt} / Нет сертиф ${productType}`;
              }
              else
              {
                return `${piece.heat} / Нет партии атт / ${piece.qcNum} ${productType}`;
              }
            }
            else
            {
              if (isGroupAttHasValue && isQcNumHasValue)
              {
                return `Нет плавки / ${piece.groupAtt} / ${piece.qcNum} ${productType}`;
              }
              else
              {
                if (isGroupAttHasValue)
                {
                  return `Нет плавки / ${piece.groupAtt} / Нет сертиф ${productType}`;
                }
                else
                {
                  return `Нет плавки / Нет партии атт / ${piece.qcNum} ${productType}`;
                }
              }
            }
          }
        }
        else
        {
          if (isHeatHasValue && isPieceHasValue)
          {
              return `${piece.heat} / ${piece.piece} ${productType}`;
          }
          else
          {
            if (!isHeatHasValue && isPieceHasValue)
            {
              return `Нет плавки / ${piece.piece} ${productType}`;
            }
            else if (isHeatHasValue)
            {
              return `${piece.heat} / Нет партии/ед ${productType}`;
            }
            else
            {
              return `Нет плавки / Нет партии/ед ${productType}`;
            }
          }
        }
      }
    }
    else
    {
      if (piece.qmetId === null || piece.qmetId === 0)
      {
        return `Нет номера ${productType}`;
      }
      else
      {
        return `${piece.qmetId} ${productType}`;
      }
    }
  }

  if (isDirtyWagons)
  {
    return piece.carriage ?? 'Нет номера';
  }

  if (isTerm)
  {
    return piece.specificationNum ?? 'Нет номера';
  }
}

// #endregion

type OpStatusChangedAction = PayloadAction<AsyncOpStatus>;
type OpStatusLabelChangedAction = PayloadAction<string>;
type FragmentChangedAction = PayloadAction<'defectlist' | 'piece' | 'complaintlist' | 'complaint'>;
type StringChangedAction = PayloadAction<string>;
type PieceChangedAction = PayloadAction<types.inspection.Piece>;
type ComplaintChangedAction = PayloadAction<{ complaintDefectGuid: string; complaint: types.inspection.Complaint }>;

export const inspectionViewSlice = createSlice({
  name: SLICE_NAME,
  initialState: { ...initialState },
  reducers: {
    ...reducers,
  },
  extraReducers: (builder) => extraReducers.forEach((creator) => creator(builder)),
})

export const inspectionViewSelectors = {
  ...selectors,
}

export const inspectionViewActions = inspectionViewSlice.actions;

export default inspectionViewSlice.reducer;
